import React, { useEffect, useState } from 'react';
import { Ticket } from 'lucide-react';
import { VondiLogo } from './ui/VondiLogo';
import { closerCodeAPI } from '../utils/api';

const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

/**
 * Lo que se va a cobrar de verdad: el precio del plan con el cupón aplicado.
 *
 * Vive aquí y se exporta para que **la pantalla y el cobro usen el mismo
 * cálculo**. Estaba escrito solo dentro de `BloquePrecio`, así que quien
 * necesitara el importe fuera de ese bloque —hoy `PaymentForm`, mañana la hoja
 * nativa de Apple Pay— tenía que recalcularlo o pasar el precio de lista. Dos
 * copias de una fórmula de dinero acaban discrepando, y el sitio donde se nota
 * es el peor: el usuario ve $650 y le cobran $550, o al revés.
 *
 * No se redondea a pesos enteros a propósito: mostrar «$550» cuando el cargo
 * va a ser de $552.50 es una diferencia chica que aparece justo donde peor se
 * ve, en el estado de cuenta.
 *
 * @returns {number} el importe a cobrar; el del plan si no hay descuento.
 */
export function precioConDescuento(plan, descuento) {
  const base = Number(plan?.amount ?? 0);
  const pct = Number(descuento?.descuento ?? 0);

  if (!(pct > 0)) return base;

  return Math.round(base * (1 - pct / 100) * 100) / 100;
}

/**
 * Piezas del checkout, calcadas de `PANTALLA_PAGO` y `PANTALLA_PAGO_SCROLL`.
 *
 * Viven aparte de la pantalla para que el orden del mockup se lea de un vistazo
 * en `SubscriptionRequired` y no se pierda entre la lógica de Stripe.
 */

/**
 * El bloque del beneficio.
 *
 * Tiene dos caras y la regla es de Ricardo (11-ago-2026): si el ahorro **cubre**
 * el plan, se enseña la cuenta completa —beneficio neto, lo ahorrado, el costo
 * tachado y cuántas veces lo recuperó—. Si **no lo cubre**, la resta daría
 * negativo y sería un mal argumento, así que solo se dice cuánto ahorró.
 */
export function BloqueBeneficio({ ahorro }) {
  if (!ahorro?.hasData) return null;

  const m = ahorro.membership;
  const cubre = Boolean(m?.coversIt);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.05)',
        borderRadius: 28,
        padding: '24px 22px 18px',
        marginBottom: 18,
        // Halo cálido a la izquierda y verde a la derecha, como en el mockup.
        boxShadow:
          '0 18px 42px -22px rgba(26,156,82,0.4), 0 0 0 6px rgba(255,255,255,0.6)',
      }}
    >
      <p style={{ textAlign: 'center', fontSize: 14, color: '#6E6E77', margin: 0 }}>
        Tu beneficio con vondi
      </p>

      {cubre ? (
        <>
          <div
            className="font-display"
            style={{
              textAlign: 'center',
              fontSize: 'clamp(46px, 14vw, 62px)',
              fontWeight: 700,
              letterSpacing: '-0.04em',
              color: '#3F9C35',
              lineHeight: 1.02,
              margin: '6px 0 2px',
            }}
          >
            +{pesos(m.gain)}
          </div>
          <p style={{ textAlign: 'center', fontSize: 14, color: '#6E6E77', margin: '0 0 18px' }}>
            de beneficio neto
          </p>

          <div style={{ display: 'flex', borderTop: '1px solid #E7E7EC', paddingTop: 14 }}>
            <Columna titulo="Has ahorrado" valor={pesos(ahorro.total)} color="#F73E48" />
            <div style={{ width: 1, background: '#E7E7EC' }} />
            <Columna titulo="Costo del acceso" valor={pesos(m.price)} color="#6E6E77" tachado />
          </div>

          {m.timesRecovered >= 2 && (
            <p
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: '#6E6E77',
                margin: '14px 0 0',
                paddingTop: 12,
                borderTop: '1px solid #E7E7EC',
              }}
            >
              Recuperaste {m.timesRecovered}× lo que pagaste
            </p>
          )}
        </>
      ) : (
        <>
          {/* Sin cubrir el plan: solo la cifra, sin restas que darían negativo. */}
          <div
            className="font-display"
            style={{
              textAlign: 'center',
              fontSize: 'clamp(46px, 14vw, 62px)',
              fontWeight: 700,
              letterSpacing: '-0.04em',
              color: '#3F9C35',
              lineHeight: 1.02,
              margin: '6px 0 2px',
            }}
          >
            {pesos(ahorro.total)}
          </div>
          <p style={{ textAlign: 'center', fontSize: 14, color: '#6E6E77', margin: 0 }}>
            es lo que llevas ahorrado
          </p>
        </>
      )}
    </div>
  );
}

function Columna({ titulo, valor, color, tachado }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontSize: 13, color: '#6E6E77', marginBottom: 2 }}>{titulo}</div>
      <div
        className="font-display"
        style={{
          fontSize: 22,
          fontWeight: 700,
          color,
          textDecoration: tachado ? 'line-through' : 'none',
        }}
      >
        {valor}
      </div>
    </div>
  );
}

/**
 * El cupón, con la forma del mockup: encabezado con ícono de boleto y, al
 * desplegarse, el campo con su botón APLICAR.
 */
export function BloqueCupon({ valor, onChange, aplicado, onValidado }) {
  const [abierto, setAbierto] = useState(Boolean(valor));

  /*
    El acordeón se abre también cuando el código llega DESPUÉS del primer
    render.

    `useState(Boolean(valor))` solo mira el valor inicial. Con el código escrito
    a mano da igual —llega vacío y lo abre la persona—, pero el que viene del
    enlace de un closer (`?code=`) se rellena tras validarlo contra el servidor,
    o sea siempre después. El resultado era que su descuento quedaba aplicado y
    **escondido detrás de un acordeón cerrado**: justo el argumento por el que
    esa persona entró al enlace.
  */
  useEffect(() => {
    if (valor) setAbierto(true);
  }, [valor]);
  /*
    APLICAR era un `<span>` decorativo.

    El código de cupón viaja con el cobro, así que técnicamente no hacía falta
    validarlo antes — pero eso es un argumento de implementación, no de uso: el
    botón se ve como botón. En la prueba, Iván lo tocó **catorce veces** sin que
    pasara nada, escribiendo su código de closer. Y su propia referencia
    (`PAGO_SIN_BENEFICIO_CANJEADO`) lo dibuja como un botón de verdad.

    Ahora valida contra el mismo endpoint público que ya usa el registro con
    `?code=`, y dice qué encontró.
  */
  const [validando, setValidando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const aplicar = async () => {
    const codigo = (valor || '').trim().toUpperCase();
    if (!codigo || validando) return;

    setValidando(true);
    setResultado(null);
    try {
      const r = await closerCodeAPI.validate(codigo);
      const bien = r?.success && r?.data?.valid;
      const dato = bien
        ? { ok: true, descuento: r.data.discount_percent, de: r.data.closer_name }
        : { ok: false };
      setResultado(dato);
      /* El precio de arriba tiene que reflejarlo: es lo que pide la referencia
         `PAGO_SIN_BENEFICIO_CANJEADO` —el precio tachado y el final ya con el
         descuento— y es lo que la persona quiere ver antes de dar su tarjeta. */
      onValidado?.(bien ? dato : null);
    } catch (e) {
      setResultado({ ok: false });
      onValidado?.(null);
    } finally {
      setValidando(false);
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(250,77,10,0.18)',
        borderRadius: 22,
        padding: '14px 16px',
        marginBottom: 20,
        boxShadow: '0 10px 26px -18px rgba(250,77,10,0.5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Ticket size={22} color="#FA4D0A" />
        <span className="font-display" style={{ flex: 1, fontSize: 16, fontWeight: 700, color: '#0E0E10' }}>
          ¿Tienes un cupón?
        </span>
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            color: '#6E6E77',
            fontSize: 14,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {abierto ? 'Ocultar' : 'Agregar'}
        </button>
      </div>

      {abierto && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#fff',
            border: '1px solid #F0F0F2',
            borderRadius: 16,
            padding: 5,
            marginTop: 12,
          }}
        >
          <input
            value={valor}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            placeholder="Ingresa tu código"
            autoCapitalize="characters"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 15,
              color: '#0E0E10',
              padding: '10px 12px',
              minWidth: 0,
            }}
          />
          <button
            type="button"
            onClick={aplicar}
            disabled={!valor || validando}
            style={{
              flex: 'none',
              background: resultado?.ok || aplicado
                ? '#1A9C52'
                : 'linear-gradient(100deg, #FA4D0A, #F73E48)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '11px 20px',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: !valor || validando ? 'default' : 'pointer',
              opacity: !valor || validando ? 0.6 : 1,
            }}
          >
            {validando ? '…' : resultado?.ok || aplicado ? 'APLICADO' : 'APLICAR'}
          </button>
        </div>
      )}

      {abierto && resultado?.ok && (
        <p style={{ fontSize: 12, color: '#1A9C52', margin: '8px 2px 0', fontWeight: 600 }}>
          Código válido{resultado.de ? ` de ${resultado.de}` : ''} · {resultado.descuento}% de descuento en tu primer pago.
        </p>
      )}

      {abierto && resultado && !resultado.ok && (
        <p style={{ fontSize: 12, color: '#E23434', margin: '8px 2px 0' }}>
          Ese código no existe o ya no está activo.
        </p>
      )}

      {abierto && !resultado && !aplicado && (
        <p style={{ fontSize: 11.5, color: '#6E6E77', margin: '8px 2px 0' }}>
          Escribe tu código y toca APLICAR.
        </p>
      )}
    </div>
  );
}

/**
 * El precio, con el cambio de modalidad.
 *
 * El mockup muestra una sola línea —«vondi · 1 año» y el precio— en vez de una
 * lista de planes. El cambio de modalidad va como un interruptor discreto
 * debajo, para respetar ese formato sin esconder la opción.
 */
export function BloquePrecio({ planes, seleccionado, onCambiar, descuento = null }) {
  const plan = planes.find((p) => p.key === seleccionado);
  if (!plan) return null;

  const otro = planes.find((p) => p.key !== seleccionado);
  const anual = planes.find((p) => p.key === 'annual');
  const mensual = planes.find((p) => p.key === 'monthly');

  /*
    Se retiró el tachado con «lo que costaría un año pagando mes a mes».

    Era una comparación honesta —$277 × 12 contra $650— pero Iván la leyó y dijo
    literalmente: «eso no lo entiendo». Y tiene razón en lo que importa: un
    número tachado junto al precio se lee como «antes costaba esto», no como una
    comparación entre modalidades. Un tachado que hay que explicar no está
    ayudando a nadie a decidir.

    En su lugar va lo que él mismo describió: cuánto te cobran y cuándo vuelven
    a cobrarte.
  */
  /*
    Con cupón válido se enseña el precio de lista tachado y el final. El cálculo
    vive en `precioConDescuento`, arriba, para que sea el mismo que usa el cobro.
  */
  const conDescuento =
    descuento?.descuento > 0 ? precioConDescuento(plan, descuento) : null;

  /*
    Texto de cadencia, dictado por Iván (13-ago-2026).

    Él escribió el mismo renglón para los dos planes —«acceso completo durante un
    año»—, pero en el mensual el cobro se renueva cada mes: prometer un año ahí
    sería una promesa que el cargo no cumple. Ricardo lo confirmó: el mensual
    dice **un mes**.
  */
  const cadencia =
    plan.interval === 'year'
      ? 'Paga y tienes acceso completo durante un año'
      : 'Paga y tienes acceso completo durante un mes';

  return (
    <div style={{ textAlign: 'center', marginBottom: 22 }}>
      <h2
        className="font-display"
        style={{ fontSize: 'clamp(19px, 5.4vw, 23px)', fontWeight: 700, color: '#0E0E10', margin: 0 }}
      >
        Compra tu acceso a{' '}
        <span style={{ display: 'inline-block', verticalAlign: '-2px' }}>
          <VondiLogo size={20} />
        </span>
      </h2>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          margin: '16px 0 6px',
        }}
      >
        <VondiLogo size={26} />
        <span className="font-display" style={{ fontSize: 20, fontWeight: 700, color: '#0E0E10' }}>
          · {plan.interval === 'year' ? '1 año' : '1 mes'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 12 }}>
        {conDescuento !== null && (
          <span
            className="font-display"
            style={{
              fontSize: 'clamp(26px, 7.6vw, 34px)',
              fontWeight: 700,
              color: '#A6A6AD',
              textDecoration: 'line-through',
            }}
          >
            {pesos(plan.amount)}
          </span>
        )}
        <span
          className="font-display"
          style={{
            fontSize: 'clamp(30px, 8.6vw, 40px)',
            fontWeight: 700,
            color: '#F73E48',
            letterSpacing: '-0.02em',
          }}
        >
          {pesos(conDescuento !== null ? conDescuento : plan.amount)}
        </span>
      </div>

      {conDescuento !== null && (
        <p style={{ fontSize: 12.5, color: '#1A9C52', margin: '6px 0 0', fontWeight: 600 }}>
          {descuento.descuento}% de descuento aplicado
          {descuento.de ? ` con el código de ${descuento.de}` : ''}
        </p>
      )}

      <p style={{ fontSize: 12.5, color: '#6E6E77', margin: '6px 0 0' }}>{cadencia}</p>

      {otro && (
        <button
          type="button"
          onClick={() => onCambiar(otro.key)}
          style={{
            marginTop: 14,
            background: '#F5F5F7',
            border: '1px solid #E7E7EC',
            borderRadius: 9999,
            padding: '9px 18px',
            fontSize: 13,
            fontWeight: 600,
            color: '#33333A',
            cursor: 'pointer',
          }}
        >
          Cambiar a {otro.label.toLowerCase()} · {pesos(otro.amount)} ·{' '}
          {otro.interval === 'year' ? 'año' : 'mes'}
        </button>
      )}
    </div>
  );
}
